import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PageHeader from '../../components/PageHeader';
import { connectionRequestsApi } from '../../services/api';
import type { ConnectionRequest, ConnectionRequestStatus, ExistingOrdType } from '../../types';
import { formatAppDateTime } from '../../utils/dateFormat';

const statusLabel: Record<ConnectionRequestStatus, string> = {
  NEW: 'Новая',
  IN_PROGRESS: 'В работе',
  DONE: 'Подключён',
  REJECTED: 'Отклонена',
};

const statusColor: Record<ConnectionRequestStatus, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
  NEW: 'primary',
  IN_PROGRESS: 'warning',
  DONE: 'success',
  REJECTED: 'default',
};

const ordLabel: Record<ExistingOrdType, string> = {
  NONE: 'Нет ОРД',
  VK: 'VK ОРД',
  YANDEX: 'Яндекс ОРД',
  OZON: 'Ozon ОРД',
  OTHER: 'Другой',
};

const ConnectionRequestsPage: React.FC = () => {
  const [items, setItems] = useState<ConnectionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ConnectionRequest | null>(null);
  const [status, setStatus] = useState<ConnectionRequestStatus>('NEW');
  const [adminNote, setAdminNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setItems(await connectionRequestsApi.list());
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openDetails = (item: ConnectionRequest) => {
    setSelected(item);
    setStatus(item.status);
    setAdminNote(item.adminNote || '');
  };

  const saveDetails = async () => {
    if (!selected) return;
    try {
      setSaving(true);
      setError(null);
      await connectionRequestsApi.update(selected.connectionRequestId, { status, adminNote });
      setSelected(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const newCount = items.filter((i) => i.status === 'NEW').length;

  return (
    <Box>
      <PageHeader
        title="Заявки на подключение"
        description="Заявки с публичной страницы TrendyORD."
        actions={
          newCount > 0 ? (
            <Chip color="primary" label={`Новых: ${newCount}`} />
          ) : undefined
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Дата</TableCell>
                <TableCell>Контакт</TableCell>
                <TableCell>Креативов/мес</TableCell>
                <TableCell>ОРД</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      Заявок пока нет
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {items.map((item) => (
                <TableRow key={item.connectionRequestId} hover>
                  <TableCell>{formatAppDateTime(item.createdTs)}</TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>{item.contactName || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {item.email || item.messenger || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>{item.creativesPerMonth ?? '—'}</TableCell>
                  <TableCell>{item.existingOrd ? ordLabel[item.existingOrd] : '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={statusLabel[item.status]} color={statusColor[item.status]} />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => openDetails(item)}>
                      Открыть
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={Boolean(selected)} onClose={() => !saving && setSelected(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Заявка #{selected?.connectionRequestId}</DialogTitle>
        <DialogContent>
          {selected && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2">
                <strong>Дата:</strong> {formatAppDateTime(selected.createdTs)}
              </Typography>
              {selected.contactName && (
                <Typography variant="body2">
                  <strong>Имя/компания:</strong> {selected.contactName}
                </Typography>
              )}
              {selected.email && (
                <Typography variant="body2">
                  <strong>Email:</strong>{' '}
                  <a href={`mailto:${selected.email}`}>{selected.email}</a>
                </Typography>
              )}
              {selected.messenger && (
                <Typography variant="body2">
                  <strong>Мессенджер:</strong> {selected.messenger}
                </Typography>
              )}
              <Typography variant="body2">
                <strong>Креативов в месяц:</strong> {selected.creativesPerMonth}
              </Typography>
              <Typography variant="body2">
                <strong>Текущий ОРД:</strong> {selected.existingOrd ? ordLabel[selected.existingOrd] : '—'}
              </Typography>
              {selected.comment && (
                <Typography variant="body2">
                  <strong>Комментарий:</strong> {selected.comment}
                </Typography>
              )}
              <FormControl fullWidth>
                <InputLabel>Статус</InputLabel>
                <Select label="Статус" value={status} onChange={(e) => setStatus(e.target.value as ConnectionRequestStatus)}>
                  {(Object.keys(statusLabel) as ConnectionRequestStatus[]).map((s) => (
                    <MenuItem key={s} value={s}>
                      {statusLabel[s]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Заметка администратора"
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                fullWidth
                multiline
                minRows={2}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)} disabled={saving}>
            Закрыть
          </Button>
          <Button variant="contained" onClick={saveDetails} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConnectionRequestsPage;
